import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowPathIcon, ChevronDownIcon, ChevronRightIcon, ClockIcon, DocumentTextIcon, FunnelIcon, QuestionMarkCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Get from "../services/Get";
import Post from "../services/Post";
import { apiError, fetchAllPages, isApiFailure, unwrapList } from "../services/providerData";
import "./P2pTrading.css";

const adId = (ad) => ad.id ?? ad.ad_id ?? ad.adId;
const adSide = (ad) => String(ad.side ?? ad.type ?? ad.ad_type ?? ad.trade_type ?? ad.transaction_type ?? ad.direction ?? "").toLowerCase();
const adAsset = (ad) => String(ad.asset ?? ad.coin ?? ad.symbol ?? ad.crypto_asset ?? ad.asset_symbol ?? ad.asset_code ?? ad.token ?? "").toUpperCase();
const adFiat = (ad) => String(ad.fiat_currency ?? ad.fiat ?? ad.currency ?? ad.fiatCurrency ?? ad.fiat_code ?? "").toUpperCase();
const adPrice = (ad) => Number(ad.price ?? ad.rate ?? ad.unit_price ?? ad.unitPrice ?? ad.price_per_unit ?? 0);
const adAvailable = (ad) => Number(ad.available_amount ?? ad.available ?? ad.remaining_amount ?? ad.available_quantity ?? ad.amount ?? 0);
const adMinimum = (ad) => Number(ad.min_order_amount ?? ad.min_amount ?? ad.min_limit ?? ad.min_order_limit ?? ad.minOrderAmount ?? 0);
const adMaximum = (ad) => Number(ad.max_order_amount ?? ad.max_amount ?? ad.max_limit ?? ad.max_order_limit ?? ad.maxOrderAmount ?? 0);
const adPayments = (ad) => {
  const value = ad.payment_methods ?? ad.paymentMethods ?? ad.payment_method ?? ad.paymentMethod ?? [];
  if (Array.isArray(value)) return value.map((item) => typeof item === "string" ? item : item?.name ?? item?.title ?? item?.method ?? "").filter(Boolean);
  if (typeof value === "string") return value.split(/[,|]/).map((item) => item.trim()).filter(Boolean);
  return [];
};
const advertiserName = (ad) => ad.advertiser?.name ?? ad.user?.name ?? ad.merchant?.name ?? ad.merchant_name ?? ad.nickname ?? ad.username ?? "P2P advertiser";
const advertiserOrders = (ad) => Number(ad.completed_orders ?? ad.order_count ?? ad.orders_count ?? ad.user?.completed_orders ?? ad.merchant?.completed_orders ?? 0);
const advertiserCompletion = (ad) => Number(ad.completion_rate ?? ad.completionRate ?? ad.user?.completion_rate ?? ad.merchant?.completion_rate ?? 0);
const advertiserRating = (ad) => Number(ad.positive_rate ?? ad.rating ?? ad.user?.positive_rate ?? ad.merchant?.positive_rate ?? 0);
const orderTime = (ad) => Number(ad.payment_time_limit ?? ad.payment_time ?? ad.time_limit ?? 15);
const responseRows = (response, kind) => unwrapList(response, kind);

const currencySymbols = { INR: "₹", USD: "$", VND: "₫", EUR: "€", GBP: "£", BRL: "R$", NGN: "₦", TRY: "₺", IDR: "Rp", PHP: "₱", MXN: "$", CAD: "$", AUD: "$" };
const assetOrder = { USDT: 0, BTC: 1, USDC: 2, FDUSD: 3, TUSD: 4, BNB: 5, ETH: 6, XRP: 7, ADA: 8 };
const formatFiat = (value, fiat) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  const symbol = currencySymbols[fiat] ?? `${fiat} `;
  return `${symbol}${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(amount)}`;
};
const formatCrypto = (value) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 8 }).format(Number(value) || 0);
const toMessage = (value) => typeof value === "string" ? value : JSON.stringify(value);

function P2pTrading() {
  const [mode, setMode] = useState("buy");
  const [ads, setAds] = useState([]);
  const [trades, setTrades] = useState([]);
  const [assets, setAssets] = useState([]);
  const [fiat, setFiat] = useState("");
  const [asset, setAsset] = useState("USDT");
  const [payment, setPayment] = useState("");
  const [sortBy, setSortBy] = useState("price");
  const [fiatAmount, setFiatAmount] = useState("");
  const [loading, setLoading] = useState(true);
  const [tradesLoading, setTradesLoading] = useState(true);
  const [adsError, setAdsError] = useState("");
  const [tradesError, setTradesError] = useState("");
  const [activeAd, setActiveAd] = useState(null);
  const [tradeAmount, setTradeAmount] = useState("");
  const [tradeLoading, setTradeLoading] = useState(false);
  const [tradeError, setTradeError] = useState("");
  const [tradeSuccess, setTradeSuccess] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);

  const loadTrades = async () => {
    setTradesLoading(true);
    setTradesError("");
    try {
      const result = await fetchAllPages(Get.P2pTrades, "trades", 50);
      if (isApiFailure(result.response)) setTradesError(toMessage(apiError(result.response)));
      else setTrades(result.items);
    } catch {
      setTradesError("Couldn't load your P2P trades.");
    } finally {
      setTradesLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    Promise.all([fetchAllPages(Get.Ads, "ads", 50), fetchAllPages(Get.P2pTrades, "trades", 50)])
      .then(([adsResult, tradesResult]) => {
        if (!active) return;
        if (isApiFailure(adsResult.response)) setAdsError(toMessage(apiError(adsResult.response)));
        else {
          const fetchedAds = adsResult.items;
          setAds(fetchedAds);
          const nextAssets = [...new Set(fetchedAds.map(adAsset).filter(Boolean))].sort((left, right) => (assetOrder[left] ?? 100) - (assetOrder[right] ?? 100) || left.localeCompare(right));
          setAssets(nextAssets);
          if (!nextAssets.includes(asset)) setAsset(nextAssets.includes("USDT") ? "USDT" : nextAssets[0] ?? "");
          const nextFiats = [...new Set(fetchedAds.map(adFiat).filter(Boolean))];
          if (!nextFiats.includes(fiat)) setFiat(nextFiats.includes("INR") ? "INR" : nextFiats[0] ?? "");
        }
        if (isApiFailure(tradesResult.response)) setTradesError(toMessage(apiError(tradesResult.response)));
        else setTrades(tradesResult.items);
      })
      .catch(() => { if (active) setAdsError("Couldn't load P2P ads. Please sign in and try again."); })
      .finally(() => { if (active) { setLoading(false); setTradesLoading(false); } });
    return () => { active = false; };
    // Initial data is loaded once; selections filter the result locally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fiatOptions = useMemo(() => [...new Set(ads.map(adFiat).filter(Boolean))], [ads]);
  const paymentOptions = useMemo(() => [...new Set(ads.filter((ad) => !fiat || adFiat(ad) === fiat).flatMap(adPayments))], [ads, fiat]);

  const visibleAds = useMemo(() => {
    const filtered = ads.filter((ad) => {
      const side = adSide(ad);
      const wantedSide = mode === "buy" ? "sell" : "buy";
      if (side && side !== wantedSide) return false;
      if (asset && adAsset(ad) !== asset) return false;
      if (fiat && adFiat(ad) !== fiat) return false;
      if (payment && !adPayments(ad).includes(payment)) return false;
      if (fiatAmount && Number(fiatAmount) > 0) {
        const entered = Number(fiatAmount);
        if (adMinimum(ad) && entered < adMinimum(ad)) return false;
        if (adMaximum(ad) && entered > adMaximum(ad)) return false;
      }
      return true;
    });
    return filtered.sort((left, right) => sortBy === "price"
      ? (mode === "buy" ? adPrice(left) - adPrice(right) : adPrice(right) - adPrice(left))
      : sortBy === "completion" ? advertiserCompletion(right) - advertiserCompletion(left)
        : advertiserRating(right) - advertiserRating(left));
  }, [ads, mode, asset, fiat, payment, fiatAmount, sortBy]);

  const visibleTrades = useMemo(() => trades.slice(0, 5), [trades]);
  const openTrade = (ad) => {
    setActiveAd(ad);
    setTradeAmount("");
    setTradeError("");
    setTradeSuccess("");
  };

  const submitTrade = async (event) => {
    event.preventDefault();
    if (!activeAd || !Number(tradeAmount) || Number(tradeAmount) <= 0) return;
    setTradeLoading(true);
    setTradeError("");
    setTradeSuccess("");
    try {
      const response = await Post.CreateTradeFromAd(adId(activeAd), { amount: Number(tradeAmount) });
      if (isApiFailure(response)) {
        setTradeError(toMessage(apiError(response)));
        return;
      }
      setTradeSuccess(response?.message ?? "P2P trade created successfully.");
      await loadTrades();
    } catch {
      setTradeError("Couldn't create this P2P trade. Please sign in and try again.");
    } finally {
      setTradeLoading(false);
    }
  };

  const cryptoAmount = activeAd && Number(tradeAmount) > 0 && adPrice(activeAd) > 0 ? Number(tradeAmount) / adPrice(activeAd) : 0;

  return (
    <div className="p2p-page">
      <Header />
      <div className="p2p-subnav">
        <div className="p2p-subnav-inner"><div className="p2p-market-tabs"><button className="selected" type="button">P2P</button><button type="button" disabled>Express</button><button type="button" disabled>Block</button></div><nav><Link to="/support-center"><QuestionMarkCircleIcon />P2P Help Center<ChevronDownIcon /></Link><Link to="/p2p-order"><DocumentTextIcon />Orders</Link><Link to="/account"><span className="p2p-nav-avatar">U</span>User Center</Link></nav></div>
      </div>

      <main className="p2p-main">
        <div className="p2p-assets-row">
          <div className="p2p-side-toggle"><button type="button" className={mode === "buy" ? "selected" : ""} onClick={() => setMode("buy")}>Buy</button><button type="button" className={mode === "sell" ? "selected sell" : ""} onClick={() => setMode("sell")}>Sell</button></div>
          <div className="p2p-asset-tabs" aria-label="Available assets">{assets.slice(0, 12).map((coin) => <button type="button" className={asset === coin ? "selected" : ""} key={coin} onClick={() => setAsset(coin)}>{coin}{coin === "USDT" && <small>Market</small>}</button>)}</div>
          {assets.length > 12 && <select aria-label="More assets" value={assets.slice(0, 12).includes(asset) ? "" : asset} onChange={(event) => event.target.value && setAsset(event.target.value)}><option value="">More assets</option>{assets.slice(12).map((coin) => <option key={coin} value={coin}>{coin}</option>)}</select>}
        </div>

        <div className="p2p-filter-row">
          <label className="p2p-amount-filter"><input inputMode="decimal" type="number" min="0" value={fiatAmount} onChange={(event) => setFiatAmount(event.target.value)} placeholder="Transaction amount" /><select aria-label="Fiat currency" value={fiat} onChange={(event) => setFiat(event.target.value)}>{fiatOptions.map((code) => <option key={code} value={code}>{code}</option>)}</select><ChevronDownIcon /></label>
          <select className="p2p-payment-select" value={payment} onChange={(event) => setPayment(event.target.value)}><option value="">All payment methods</option>{paymentOptions.map((method) => <option key={method} value={method}>{method}</option>)}</select>
          <button type="button" className={`p2p-filter-button ${filterOpen ? "selected" : ""}`} aria-label="Toggle filters" onClick={() => setFilterOpen(!filterOpen)}><FunnelIcon /></button>
          <label className="p2p-sort-select">Sort by <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}><option value="price">Price</option><option value="completion">Completion rate</option><option value="rating">Rating</option></select><ChevronDownIcon /></label>
        </div>
        {filterOpen && <div className="p2p-extra-filters"><span>{visibleAds.length} matching ads</span><button type="button" onClick={() => { setFiatAmount(""); setPayment(""); setSortBy("price"); }}>Reset filters</button></div>}

        <section className="p2p-advertisements">
          <div className="p2p-table-head"><span>Advertisers</span><span>Price</span><span>Available / Order Limit</span><span>Payment</span><span>Trade</span></div>
          {loading && <div className="p2p-state"><ArrowPathIcon className="spin" />Loading P2P ads…</div>}
          {!loading && adsError && <div className="p2p-state p2p-error">{adsError}</div>}
          {!loading && !adsError && visibleAds.length === 0 && <div className="p2p-state">No {mode} ads match these filters.</div>}
          {!loading && !adsError && visibleAds.map((ad) => {
            const methods = adPayments(ad);
            const orders = advertiserOrders(ad);
            const completion = advertiserCompletion(ad);
            const rating = advertiserRating(ad);
            const action = mode === "buy" ? `Buy ${asset}` : `Sell ${asset}`;
            return <article className="p2p-ad-row" key={adId(ad) ?? `${adAsset(ad)}-${adPrice(ad)}-${advertiserName(ad)}`}>
              <div className="p2p-advertiser"><div className="p2p-advertiser-name"><span className="p2p-avatar">{advertiserName(ad).slice(0, 1).toUpperCase()}</span><strong>{advertiserName(ad)}</strong>{(ad.is_verified || ad.merchant?.is_verified) && <span className="p2p-verified">✓</span>}</div><div className="p2p-advertiser-meta"><span>{orders.toLocaleString()} orders</span><i /> <span>{completion ? `${completion}%` : "—"} completion</span></div><div className="p2p-advertiser-meta"><span>👍 {rating ? `${rating}%` : "—"}</span><i /><span><ClockIcon />{orderTime(ad)} min</span></div></div>
              <div className="p2p-price"><strong>{formatFiat(adPrice(ad), adFiat(ad))}</strong>{ad.label && <small>{ad.label}</small>}</div>
              <div className="p2p-available"><strong>{formatCrypto(adAvailable(ad))} {adAsset(ad)}</strong><span>{formatFiat(adMinimum(ad), adFiat(ad))} – {formatFiat(adMaximum(ad), adFiat(ad))}</span></div>
              <div className="p2p-payments">{methods.length ? methods.map((method) => <span key={method}>{method}</span>) : <span>Payment method details unavailable</span>}</div>
              <button className={`p2p-trade-action ${mode}`} type="button" onClick={() => openTrade(ad)}>{action}</button>
            </article>;
          })}
        </section>

        <section className="p2p-recent-trades">
          <div className="p2p-recent-heading"><div><h2>My Recent P2P Trades</h2><p>Trades created from your P2P ads</p></div><div><button type="button" aria-label="Refresh trades" onClick={loadTrades}><ArrowPathIcon /></button><Link to="/p2p-order">All orders <ChevronRightIcon /></Link></div></div>
          {tradesLoading ? <div className="p2p-state">Loading trades…</div> : tradesError ? <div className="p2p-state p2p-error">{tradesError}</div> : visibleTrades.length ? <div className="p2p-trade-list">{visibleTrades.map((trade, index) => <div className="p2p-trade-history-row" key={trade.id ?? index}><span>{trade.order_number ?? trade.trade_no ?? `Trade #${trade.id ?? index + 1}`}</span><span>{trade.side ?? trade.type ?? "P2P"} {trade.asset ?? trade.coin ?? ""}</span><span>{trade.amount ?? "—"} {trade.asset ?? trade.coin ?? ""}</span><span className={`trade-status ${String(trade.status ?? "").toLowerCase()}`}>{trade.status ?? "Pending"}</span><Link to={`/p2p-order?trade=${trade.id}`}>Details</Link></div>)}</div> : <div className="p2p-state">No P2P trades yet.</div>}
        </section>
      </main>
      <Footer />

      {activeAd && <div className="p2p-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setActiveAd(null); }}><section className="p2p-trade-modal" role="dialog" aria-modal="true" aria-labelledby="p2p-trade-title"><div className="p2p-modal-heading"><div><h2 id="p2p-trade-title">{mode === "buy" ? "Buy" : "Sell"} {asset}</h2><p>Offer from {advertiserName(activeAd)}</p></div><button type="button" aria-label="Close" onClick={() => setActiveAd(null)}><XMarkIcon /></button></div><div className="p2p-modal-summary"><span>Price</span><strong>{formatFiat(adPrice(activeAd), adFiat(activeAd))} / {asset}</strong><span>Order limit</span><strong>{formatFiat(adMinimum(activeAd), adFiat(activeAd))} – {formatFiat(adMaximum(activeAd), adFiat(activeAd))}</strong></div><form onSubmit={submitTrade}><label htmlFor="p2p-order-amount">{adFiat(activeAd)} amount</label><div className="p2p-modal-amount"><input id="p2p-order-amount" type="number" inputMode="decimal" min={adMinimum(activeAd) || undefined} max={adMaximum(activeAd) || undefined} step="any" value={tradeAmount} onChange={(event) => setTradeAmount(event.target.value)} placeholder="Enter transaction amount" required /><span>{adFiat(activeAd)}</span></div><div className="p2p-modal-estimate">Estimated {asset}: {formatCrypto(cryptoAmount)}</div>{tradeError && <p className="p2p-error">{tradeError}</p>}{tradeSuccess && <p className="p2p-success">{tradeSuccess}</p>}<button className={`p2p-confirm-button ${mode}`} type="submit" disabled={tradeLoading || !Number(tradeAmount) || (adMinimum(activeAd) > 0 && Number(tradeAmount) < adMinimum(activeAd)) || (adMaximum(activeAd) > 0 && Number(tradeAmount) > adMaximum(activeAd))}>{tradeLoading ? "Submitting…" : `Confirm ${mode}`}</button></form><div className="p2p-modal-note"><QuestionMarkCircleIcon />Only complete payment through the order page. Keep all trade communication in the platform.</div></section></div>}
    </div>
  );
}

export default P2pTrading;
